import React from 'react';
import { Container, Typography, List, ListItem, ListItemIcon, ListItemText, Grid, Box, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

const officeHours = [
  { id: 1, icon: <AccessTimeIcon />, text: 'Monday to Friday: 08h30 - 18h00' },
  { id: 2, icon: <AccessTimeIcon />, text: 'Saturday: 08h30 - 13h00' },
  { id: 3, icon: <AccessTimeIcon />, text: 'Sunday & Public Holidays: Closed' },
];

const contactInfo = [
  { id: 1, icon: <HomeIcon />, text: '62 Burnham Road, Plumstead (opposite Mediclinic Constantiaberg) ' },
  { id: 2, icon: <PhoneIcon />, text: '(021) 001 1190' },
  { id: 3, icon: <EmailIcon />, text: 'reception@constantiamed.co.za' },
];

export default function Contact() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        paddingTop: '120px',
        paddingBottom: '80px',
        background: 'linear-gradient(135deg, rgba(14, 84, 109, 0.05) 0%, rgba(22, 178, 139, 0.05) 100%)',
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Fade in={true} timeout={1000}>
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              Contact Us
            </Typography>
          </Box>
        </Fade>

        <Grid container justifyContent="center" spacing={4}>
          {/* Office Hours */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '32px',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                height: '100%'
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Office Hours
              </Typography>
              <List>
                {officeHours.map((item) => (
                  <ListItem key={item.id} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ color: theme.palette.secondary.main }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '1.1rem',
                          fontWeight: 500
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Contact Details */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '32px',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                height: '100%'
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Contact Details
              </Typography>
              <List>
                {contactInfo.map((item) => (
                  <ListItem key={item.id} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ color: theme.palette.secondary.main }}>
                      {item.icon}
                    </ListItemIcon>
                    {item.text === 'reception@constantiamed.co.za' ? (
                      <ListItemText>
                        <a
                          href={`mailto:${item.text}`}
                          style={{
                            textDecoration: 'none',
                            color: theme.palette.primary.main,
                            fontSize: '1.1rem',
                            fontWeight: 500,
                            transition: 'color 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.color = theme.palette.secondary.main}
                          onMouseLeave={(e) => e.target.style.color = theme.palette.primary.main}
                        >
                          {item.text}
                        </a>
                      </ListItemText>
                    ) : (
                      <ListItemText
                        primary={item.text}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '1.1rem',
                            fontWeight: 500
                          }
                        }}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Map */}
          <Grid item xs={12}>
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '24px',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
            >
              <Typography
                variant="h4"
                align="center"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Find Us
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: '500px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: '<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6613.716050687659!2d18.449455348513407!3d-34.021855078021034!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc43520b5c88e9%3A0x61807c9ddfbcd80e!2sConstantia%20Medisuites!5e0!3m2!1sen!2sza!4v1692096505029!5m2!1sen!2sza" width="100%" height="500" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>',
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
